import React, { useEffect, useState } from "react";
import { Card, Button, Table, Form, Alert, Pagination } from "react-bootstrap";
import { IconTrash } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/auth";
import { Helmet } from "react-helmet-async";

const TaskManager = () => {
  const [tasks, setTasks] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [page, limit, search]);

  const getPaginationPages = (current, total) => {
    const delta = 2;
    const pages = new Set();
    pages.add(1);
    pages.add(total);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) pages.add(i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      result.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };

  const fetchUser = async () => {
    try {
      const res = await fetchWithAuth("/api/auth/profile");
      const data = await res.json();
      setIsAdmin(data.role === "admin");
    } catch (e) {
      console.error("Profile error", e);
    }
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(`/api/celery-tasks?limit=${limit}&page=${page}&search=${encodeURIComponent(search)}`);
      const data = await res.json();
      setTasks(data.tasks || []);
      setTotalCount(data.total_count || 0);
      setTotalPages(Math.ceil((data.total_count || 1) / limit));
    } catch (e) {
      setError("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedTasks((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const deleteSelected = async () => {
    if (selectedTasks.length === 0) return;
    const confirmDelete = window.confirm("Delete selected tasks?");
    if (!confirmDelete) return;

    for (let id of selectedTasks) {
      await fetchWithAuth("/api/celery-tasks/" + id, { method: "DELETE" });
    }
    setSelectedTasks([]);
    fetchTasks();
  };

  // BULK PURGE buttons
  const purgeAll = async () => {
    if (!window.confirm("Purge ALL task logs?")) return;
    await fetchWithAuth("/api/celery-tasks/purge/all", { method: "DELETE" });
    setPage(1);
    fetchTasks();
  };

  const purge30 = async () => {
    if (!window.confirm("Purge tasks older than 30 days?")) return;
    await fetchWithAuth("/api/celery-tasks/purge/older-than-30d", { method: "DELETE" });
    setPage(1);
    fetchTasks();
  };

  const purge90 = async () => {
    if (!window.confirm("Purge tasks older than 90 days?")) return;
    await fetchWithAuth("/api/celery-tasks/purge/older-than-90d", { method: "DELETE" });
    setPage(1);
    fetchTasks();
  };

  return (
    <>
      <Helmet><title>Task Manager - IndexBooster</title></Helmet>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h3 className="m-0">Celery Tasks</h3>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Form.Control
              type="text"
              placeholder="Search by URL..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="sm"
              style={{ maxWidth: "300px" }}
            />
            <Form.Select
              size="sm"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              style={{ width: "auto" }}
            >
              {[10, 25, 50, 100].map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </Form.Select>

            {isAdmin && (
              <>
                <Button variant="outline-danger" size="sm" onClick={purgeAll} disabled={loading}>
                  Purge All
                </Button>
                <Button variant="outline-warning" size="sm" onClick={purge30} disabled={loading}>
                  Purge &gt;30d
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={purge90} disabled={loading}>
                  Purge &gt;90d
                </Button>
              </>
            )}
          </div>
        </Card.Header>

        {error && <Alert variant="danger" className="m-3">{error}</Alert>}

        <div className="table-responsive">
          <Table className="table card-table table-vcenter text-nowrap datatable">
            <thead>
              <tr>
                {isAdmin && (
                  <th className="w-1">
                    <Form.Check
                      type="checkbox"
                      onChange={(e) => setSelectedTasks(e.target.checked ? tasks.map((t) => t.id) : [])}
                      checked={tasks.length > 0 && selectedTasks.length === tasks.length}
                    />
                  </th>
                )}
                <th>Task ID</th>
                <th>Task Name</th>
                <th>Queue</th>
                <th>Worker</th>
                <th>Created At</th>
                <th>URL</th>
                <th>Project</th>
                <th>User</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 ? (
                <tr><td colSpan="9" className="text-center py-4">{loading ? "Loading..." : "No data"}</td></tr>
              ) : tasks.map((task) => (
                <tr key={task.id}>
                  {isAdmin && (
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedTasks.includes(task.id)}
                        onChange={() => toggleSelect(task.id)}
                      />
                    </td>
                  )}
                  <td>{task.task_id}</td>
                  <td>{task.task_name}</td>
                  <td>{task.queue}</td>
                  <td>{task.worker}</td>
                  <td>{new Date(task.created_at).toLocaleString()}</td>
                  <td>{task.url ? <a href={task.url} target="_blank" rel="noreferrer">{task.url}</a> : "-"}</td>
                  <td>{task.project ? <a href={`/project/${task.project.id}`}>{task.project.name}</a> : "-"}</td>
                  <td>{task.user ? <a href={`/user/${task.user.id}/projects`}>{task.user.email}</a> : "-"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <Card.Footer className="d-flex justify-content-between align-items-center">
          {isAdmin && (
            <Button variant="danger" onClick={deleteSelected} disabled={selectedTasks.length === 0 || loading}>
              <IconTrash size={16} className="me-1" />
              Delete selected
            </Button>
          )}

          <Pagination className="m-0">
            <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)}>«</Pagination.Prev>
            {getPaginationPages(page, totalPages).map((p, i) =>
              p === "..." ? (
                <Pagination.Ellipsis key={`ellipsis-${i}`} disabled />
              ) : (
                <Pagination.Item key={`page-${p}`} active={p === page} onClick={() => setPage(p)}>
                  {p}
                </Pagination.Item>
              )
            )}
            <Pagination.Next disabled={page === totalPages} onClick={() => setPage(page + 1)}>»</Pagination.Next>
          </Pagination>
        </Card.Footer>
      </Card>
    </>
  );
};

export default TaskManager;
